/**
 * 「记一笔」的**草稿模型与提交映射**（纯逻辑，**零 DOM**）。
 *
 * ## 🔴 为什么把它从页面里抽出来
 *
 * 页面（`NewTransactionPage`）负责"长什么样"，本文件负责"**草稿 → 请求**"。
 * 抽出来的两个理由：
 *
 * 1. **可测**：多腿 / 转账的校验分支多（腿数上限 / 资金出口 / 转账形态 / 同币种），
 *    而这些判定**全是纯函数输入输出** ⇒ 抽出来后能用零依赖基座直接断言，
 *    不必渲染页面（见 `txn-draft.spec.ts`）。
 * 2. 🔴 **防双实现**：形态判定的唯一实现是共享层内核（`checkLegShape` =
 *    `checkCompositeShape` + `checkFundSingleOutlet` + `checkTransferShape`）。
 *    本文件**只喂数据、只转发内核的文案**，一句形态规则都不重写 ——
 *    否则端侧会与服务端各判一套（铁律 24）。
 *
 * ## 边界：哪些是本文件自己的规则、哪些不是
 *
 * | 规则 | 谁定 | 为什么 |
 * |---|---|---|
 * | 腿数上限 / 资金出口唯一 / 转账形态 | **共享层内核** | 服务端写库前调同一个函数 |
 * | 金额文本必须合法且 **> 0** | 本文件 | 金额是"用户输入的事实"，方向另由 `direction` 表达；0 元腿无意义 |
 * | **转账两端币种必须相同** | 本文件（**P0 范围限制**） | 跨币种转账是**另一个形态**（`DR-004` §2.5：含汇兑损益腿），P0 不做 ⇒ 与其记成"两腿等额但币种不同"的错数据，不如**录入期拦下并说明** |
 */

import {
  LegDirection,
  ParentRowKind,
  centsToYuanText,
  checkLegShape,
  finalDiscounts,
  paidAmounts,
  storedLegOriginal,
  sumOriginals,
  toCents,
  yuanTextToCents,
  type Cents,
  type CreateTransactionRequest,
  type SalaryMode,
  type ShapeLeg,
  type TransactionDetailView,
  type TransactionLegView,
} from '@jiaying/shared';

/** 三种录入形态（**UI 层的分组方式**，不等于数据库里的形态 —— 后者由腿集派生） */
export type DraftForm = 'single' | 'transfer' | 'multi';

/** 一条待提交的腿（**全部字段仍是"用户输入态"**，金额是文本、未换算） */
export interface DraftLeg {
  accountId: string;
  direction: LegDirection;
  /**
   * **原价**的十进制字面量（`"123.45"`）；换算走 `yuanTextToCents`，见 `buildRequest`。
   *
   * 🔴 `DR-084` 起这一格是**账单上那一项的价格**（原价，不是实付）：
   * 实付由 `buildRequest` 派生（`原价 − 自填 − 整单分摊`）后作为 `amount` 落库。
   * 修前（批 `117`–`149`）这一格是**实付**、优惠只作记录 ⇒ 用户照账单填原价会把支出记多
   * （实测：原价 46.95 / 优惠 0.53 被记成 46.95，而不是 46.42）。
   */
  amountText: string;
  /** `null` = 未分类（「先记后分类」是允许的） */
  categoryId: string | null;
  /**
   * 腿级「不计收支」—— 缺省 = **计入**。
   *
   * 🔴 它存在的原因是**工资条**：`DR-016` §3.3 的同一笔交易里，社保缴费 / 个税（计入）
   * 与医保 / 公积金（**转账子项、不计收支**）**混在一起** —— 只有腿级标记能表达，
   * 交易级标记会把整笔都排除掉。判定时的有效值 = 本标记 ∨ 交易级（见 `isCountedInStats`）。
   */
  excludeFromStats?: boolean;
  /**
   * 🆕 **子笔备注**（`DR-043`：拆分子笔 = 分类 / 金额 / 优惠 / 备注）。
   *
   * 🔴 与**交易级** `TxnDraft.note` 是两个字段：本条只描述这一笔细分
   * （如「食材」那笔写"周末采购"），交易级备注描述整单。
   * 载体早已存在（`legs.note` · `LegInputSchema.note`），此前只是录入界面没给入口
   * （铁律 53：**没入口 = 没兑现**）。
   */
  note?: string;
  /**
   * 🆕 **子笔「优惠」**的十进制字面量（缺省 / 空串 = 无优惠）。
   *
   * 语义 = **减免**（`DR-040` D3：钱当场少花、**从未流出**）。
   * 🔴 提交时换算成整数分 `discount`；这一格是**这一行自己的**优惠，
   * 与父级「整单优惠」分摊下来的那份**相加**后落库（`DR-054` D3 / `DR-084` D4）。
   */
  discountText?: string;
  /**
   * 🔴 **这个优惠值是"上次落库的结果"**（编辑回填），不是用户本次自填（`DR-084` D4）。
   *
   * 为什么需要它：库里的 `legs.discount` 是「自填 + 分摊」**合流后的一个数**，
   * 回填时无法拆分 ⇒ 用户打开编辑页再填一次整单优惠，就会把旧值当成自填**再扣一次**
   * （2026-11-04 实测：0.53 → 1.06，而金额不动 ⇒ 完全看不出来）。
   *
   * ⇒ 规则：**填了整单优惠 ⇒ 带本标记的值退出自填**（视为"上次已摊掉"）；
   * 用户手动改这一格 ⇒ 标记清掉（变成真自填，按 `DR-054` 叠加）。
   */
  discountFromStore?: boolean;
}

/** 一张表单的完整状态 */
export interface TxnDraft {
  form: DraftForm;
  date: string;
  note: string;
  /**
   * 🆕 标签（批 `147` · C 域 PRD §5.4 · `DR-082`）。
   *
   * 语义是**整体替换**（契约 `tagIds` 与 `PATCH` 同纪律）：提交时**总是发**本数组，
   * 空数组 = 清空标签。⚠️ 这与"不发 = 不改"的字段（`status` / `parentId`）不同 ——
   * 那些是**本页没有入口**的字段，而标签从本批起**有入口**了。
   *
   * 🔴 为什么放在草稿而不是页面 state：编辑路径（`editDraft`）要把它从详情里**搬进来**，
   * 否则会出现"打开编辑页看不到原标签、保存一次就全没了"（`AC-C1.42` 预填完整性）。
   */
  tagIds: string[];
  /** `single` / `multi` 使用；`transfer` 不看它（两端由下面的字段表达） */
  legs: DraftLeg[];
  /** `transfer` 专用：转出账户（资金流出，`out` 腿） */
  fromAccountId: string;
  /** `transfer` 专用：转入账户（资金流入，`in` 腿） */
  toAccountId: string;
  /** `transfer` 专用：转账金额（**两端共用同一个数** —— 同币种转账两腿必等额） */
  transferAmountText: string;
  /**
   * 🆕 **整单优惠**的十进制字面量（批次 `94` · `DR-040` §4.2 / C 域 PRD 🆕9）。
   *
   * 语义与子笔 `discountText` 相同（**减免**），区别只在**作用域**：
   * 它属于整单，落库前按**各行原价比例**分摊进各腿的 `discount`（**不落库**）。
   *
   * 🔴 只对「拆分」有意义（同一账户同向、≥ 2 行）；单笔/工资条/转账下为空。
   * 🔴 最终每腿 `discount` = 子笔自填 + 分摊额（负责人 2026-10-21 拍板：子笔仍可自填）——
   *    合流点唯一在共享层 `finalDiscounts()`，端侧**不另算一遍取整**。
   */
  splitDiscountText?: string;
  /**
   * 🔴 **退款专线**：被冲减的原交易 id（`DR-035` v1.1 · C 域 PRD §5.2.3）。
   *
   * | 取值 | 含义 |
   * |---|---|
   * | `null` / `undefined` | 普通记账（**绝大多数**） |
   * | `string` | 本笔是**退款**，冲减该笔原支出 ⇒ 请求带 `parentId` |
   *
   * 🔴 **它不由用户手填**：入口是「从某笔支出发起退款」（`/new?refundOf=<id>`），
   * 由那个入口把 id 塞进草稿 —— 这正是 C 域 PRD 说的「**前端引导**」。
   * ⚠️ 服务端**不强制**（退款与报销型理赔数据同形，硬拦会把合法理赔拦掉）；
   * 且挂不挂**不影响任何计算**（冲减腿按**分类**计入）⇒ 本字段只服务"数据可追溯"。
   */
  refundParentId?: string | null;
  /**
   * 🆕 **工资条的录入口径**（批 141 · `DR-016` §3.6 / C 域 PRD §5.2.1⑤）。
   *
   * | 取值 | 含义 |
   * |---|---|
   * | `'simple'` | 只记**到手的钱**（一条工资收入），**未含社保与个税** |
   * | `undefined` | 普通交易 / **完整工资条**（多腿结构本身就是事实） |
   *
   * 🔴 只在用户点了「只记到手这一笔」之后设置；切走「收入」模式由 `applyMode` 清掉
   * —— 留着会变成"一笔支出带着工资条标记"这种看不见却会提交的值。
   */
  salaryMode?: SalaryMode;
  /**
   * 🆕 **垫付专线**：这笔支出是替谁垫的（`DR-048` D7 · `AC-Q3.2`）。
   *
   * | 取值 | 含义 |
   * |---|---|
   * | `''` / `undefined` | 普通支出（**绝大多数**） |
   * | `string` | 该 `contra` 账户 id ⇒ 请求额外带一条 `in` 腿 |
   *
   * ## 🔴 为什么会多出一条腿
   *
   * `AC-Q3.2` 原文：垫付 = 2 腿 —— `银行 out + 原支出分类（计入）` ＋ `报销账户 in（不计）`。
   * 第二腿的金额**恒等于**第一腿（垫出去多少就是多少），所以**不让用户填第二次** ——
   * 那不是"两个数要相等"的校验，而是**一件事的两个面**。
   *
   * ## 它为什么只允许「单笔支出」
   *
   * 拆分（同一账户多笔）与工资条里"垫付"没有意义：拆分的每一笔都该有自己的垫付关系，
   * 而工资条是收入侧。⇒ 多腿形态下本字段被忽略并报错（见 `buildRequest`）。
   */
  advanceAccountId?: string;
  /**
   * 🆕 **记账时分期**（`DR-067` · 批 `116`）—— 三个字段合起来描述"这笔刷完当场分几期"。
   *
   * | 字段 | 含义 |
   * |---|---|
   * | `installmentPeriods` | 期数（`undefined` = **不分期**，与今天完全一致） |
   * | `installmentFeeMode` | `total` 总手续费 / `per_period` 每期手续费（缺省 `total`） |
   * | `installmentFeeText` | 手续费的十进制字面量（空串 = 免息） |
   *
   * 🔴 **本金与首期账单日不在这里**：本金 = 本笔金额、首期账单日 = 该卡下一个账单日，
   * 都由**服务端**定（`DR-067` D2）—— 端侧没有"另填一个本金"的入口，就不存在
   * 「本金 ≠ 消费额」的对账缺口。
   *
   * 🔴 只对**单笔支出**有意义：`applyMode` 切走「支出」或打开拆分时**清空**这三个字段
   * （留着 = 用户看不见却会提交的值，最坏的一类"静默生效"）。
   */
  installmentPeriods?: number;
  installmentFeeMode?: 'total' | 'per_period';
  installmentFeeText?: string;
}

export function emptyLeg(accountId = ''): DraftLeg {
  return { accountId, direction: LegDirection.Out, amountText: '', categoryId: null };
}

/** 初始草稿（账户预选第一个日常账户 —— 与旧版单腿行为一致，减少一次点击） */
export function initialDraft(date: string, accountId = ''): TxnDraft {
  return {
    form: 'single',
    date,
    note: '',
    tagIds: [],
    legs: [emptyLeg(accountId)],
    fromAccountId: accountId,
    toAccountId: '',
    transferAmountText: '',
  };
}

/** 「从某笔支出发起退款」时，被冲减原交易的**最小投影**（服务端 `TransactionRowView` 的子集） */
export interface RefundSource {
  readonly id: string;
  /** 原交易所在的账户 —— 退款默认退到**同一个账户**（钱从哪儿回哪儿） */
  readonly accountId: string | null;
  readonly note: string | null;
  readonly date: string;
  /** 原交易净额（原币整数分，流出为负）—— 仅用于**展示**"最多可退"参考值 */
  readonly net: number | null;
  /**
   * 🆕 原交易的**支出分类**（批 `119` · 必填，`null` = 原交易当时没分类）。
   *
   * 🔴 退款腿的方向是 `in`，但它必须挂**支出**分类才被内核 `isRefundTxn()` 认成退款
   * （挂收入分类 = 一笔普通收入 ⇒ 服务端 `bindRefundToParent()` 400）。
   * ⇒ 由调用方用 `refundCategoryId()`（`features/refund-entry.ts` 的唯一实现）算好传进来，
   * 这里只负责预填 —— 端侧不自己判"哪条腿是支出腿"。
   */
  readonly categoryId: string | null;
  /**
   * 🆕 这笔消费**已经退了多少**（批 `120` · `DR-069` D6，正数整数分；没退过 = 0）。
   *
   * 只用于在退款页显示「最多可退 = 原额 − 已退」。修前 `net` 的注释就写着"用于最多可退"，
   * 但**全仓没有任何地方渲染它**（铁律 53：没入口 = 没兑现）—— 部分退款后再退，用户没有参考数。
   */
  readonly refundedAmount: number;
  /**
   * 🆕 批 `154`：从**某一条腿**发起（拆分项的单项退款）时，**该腿所属分类**的剩余可退。
   *
   * 🔴 为什么单独给而不是复用 `net` / `refundedAmount`：那两个是**整笔**口径。
   * 一笔「食品 120 + 日用 80」在退过日用 80 之后，整笔剩余 120，
   * 而「日用这一项」的剩余是 **0** —— 用整笔数预填会让用户再退一次日用。
   */
  readonly legLeftCents?: number;
}

/**
 * **剩余可退** = `|原交易净额| − 已退合计`（原币整数分，不小于 0）。
 *
 * 🆕 批 `129`：这是「最多可退」的**唯一实现** —— 三个消费者共用：
 * ① 退款页横幅的「最多可退 X」；② 退款页金额栏的**默认值**（批 129 起预填）；
 * ③ 服务端 `bindRefundToParent()` 的上限校验（口径同一条，只是那边用库里的腿重算）。
 *
 * 🔴 为什么不在这里减优惠：`RefundSource.net` 是**服务端下发的派生净额**
 * （`parent.net`，只按腿算、不含 `discount`）—— `DR-069` D2 明文「净额不再二次减优惠」。
 * 端侧再减一次就会出现"退款页说还能退 50、服务端说只剩 49.5"这类无法解释的差。
 */
export function refundLeftCents(source: RefundSource): number {
  const original = Math.abs(source.net ?? 0);
  const left = original - source.refundedAmount;
  return left > 0 ? left : 0;
}

/**
 * 退款草稿（`DR-035` v1.1 · C 域 PRD §5.2.3「前端引导」）。
 *
 * ## 它把哪些东西一次性摆正
 *
 * | 项 | 退款要求 | 为什么在这里做 |
 * |---|---|---|
 * | **方向** | `in` 腿（钱**回来**） | 退款天然反向于原支出，让用户再选一次方向就会有人选错 |
 * | **账户** | 默认同原账户 | 钱从哪儿回哪儿；跨账户退款仍允许（用户可改） |
 * | **`parentId`** | 指向原交易 | 这是"退款"区别于"报销型理赔"的唯一线索，且**用户无从手填** |
 * | **拆分** | **关**（退款是单笔） | 退款本身**不可再被冲减**，且拆分会让 `parentId` 语义含混 |
 * | **金额** | 🆕 批 `129` 起**预填剩余可退** | 负责人 2026-10-31：「在记录退款的时候默认全额退款，金额栏写原先支付金额」。<br>绝大多数退款是**全额退**，空着让用户自己输原额既不顺手、又输错就得多记一笔更正 |
 *
 * 🔴 **默认值 ≠ 限制**：预填的是"还能退多少"，用户可以改小（`AC-C2.14` 支持多次部分退款）；
 * 改大才会被服务端拒（`剩余可退` 是硬上限）。金额栏始终可编辑。
 *
 * 🔴 **本函数不做形态校验** —— 退款形态（`in` 腿 + 挂**支出**分类）由服务端
 * 用**库里的腿**判（`isRefundTxn` + `checkRefundParent`，见 `transaction-write.service.ts`）。
 * 端侧只负责把"这是一笔退款"这个**意图**摆进草稿，不自建判定（铁律 24）。
 */
export function refundDraft(source: RefundSource, date: string): TxnDraft {
  // 🆕 批 154：单项退款用**该腿**的剩余（整笔剩余会把别的项已退的额度让给它）
  const left = source.legLeftCents ?? refundLeftCents(source);
  return {
    form: 'single',
    date,
    // 备注预填原备注便于对照（用户会改）；空备注不伪造
    note: source.note ?? '',
    // 🆕 批 `147`：退款不继承原交易的标签（标签描述的是"这一笔的场景"，
    //    退款是新的场景）⇒ 空数组 = 由用户在退款页自行决定要不要打标签。
    tagIds: [],
    legs: [
      {
        accountId: source.accountId ?? '',
        // 🔴 方向恒为 `in` —— 退款是"钱回来"，与用户点哪个按钮无关
        direction: LegDirection.In,
        // 🆕 批 `129`：预填**剩余可退**（已部分退过 ⇒ 只剩差额）。
        //   ⚠️ `left === 0` ⇒ 留空：全额退完的入口在端侧已被 `canRefund()` 挡住，
        //   这里兜底成空串，避免把 `0` 填进金额栏后被当成"0 元退款"直接提交。
        amountText: left === 0 ? '' : centsToYuanText(toCents(left)),
        // 🆕 批 `119`：预填**原支出分类**（退款必须挂支出分类，否则 `isRefundTxn` 判成普通收入）
        categoryId: source.categoryId,
      },
    ],
    fromAccountId: source.accountId ?? '',
    toAccountId: '',
    transferAmountText: '',
    refundParentId: source.id,
  };
}

/** 形态切换时**保留已有输入**（人常在"记完发现是转账"时才切形态） */
export function switchForm(draft: TxnDraft, form: DraftForm): TxnDraft {
  if (form === draft.form) return draft;
  if (form === 'single') {
    // 单腿：只留第一条腿（它的账户/金额/分类就是"这一笔"）
    const first = draft.legs[0] ?? emptyLeg(draft.fromAccountId);
    return { ...draft, form, legs: [{ ...first, direction: LegDirection.Out }] };
  }
  if (form === 'transfer') {
    const first = draft.legs[0];
    return {
      ...draft,
      form,
      fromAccountId: draft.fromAccountId === '' ? (first?.accountId ?? '') : draft.fromAccountId,
      toAccountId: draft.toAccountId,
      transferAmountText:
        draft.transferAmountText === '' ? (first?.amountText ?? '') : draft.transferAmountText,
    };
  }
  // multi：至少 2 条腿（1 条的"多腿"没有意义，直接给第二条）
  const legs = draft.legs.length >= 2 ? draft.legs : [...draft.legs, emptyLeg(draft.fromAccountId)];
  return { ...draft, form, legs };
}

/**
 * 录入界面的**模式**（= 顶部那排同级按钮的取值）。
 *
 * 🔴 它与 `DraftForm` **不是一回事**：`out` / `in` 默认落 `single`，
 * 但打开「拆分」开关后落 `multi`。分开是因为「怎么记」是用户的高频心智，
 * 而「形态」是数据库的实现细节 —— 混成一个枚举会逼用户理解"腿"。
 *
 * ## 🔴 演进史（两次都被负责人的反馈推翻，记录理由防再犯）
 *
 * | 版本 | 按钮 | 问题 |
 * |---|---|---|
 * | 初版 | 支出 / 收入 / 转账 / **多条腿** | 「腿」是 `legs` 表的数据模型词，**用户看不懂** |
 * | ㊲ 批 | 支出 / **拆分** / 收入 / **工资条** / 转账 | 拆分被提成**同级**按钮 —— 但它**不是一种记账种类**，只是"这笔支出要不要分几处记"的开关 |
 * | **本版** | **支出 / 收入 / 转账 / 工资条** + 支出与收入**各自内部**的「拆分」开关 | ✅ |
 *
 * ## 🔴 为什么「拆分」是开关而不是同级按钮（负责人 2026-10-04 的原话）
 *
 * > 「拆分收支是银行对账单就一笔支出，但是会细分到不同类别，
 * >  所以只要在支出或收入的地方做个开关，拆分就行。
 * >  **但是必须是一个方向，要么支出，要么收入，因为父项在银行对账单就一笔**」
 *
 * 这段把「拆分」的本质说清楚了：它是**一笔**（对账单上就一行）在**账本内部**分了几处。
 * ⇒ 它**从属于**某个方向，不是并列于「支出 / 收入」的第四种东西。
 *
 * ## 🔴 「一个方向」是**硬约束**，不是建议
 *
 * 父项在对账单上只有一笔 ⇒ 它的所有子项天然同向。
 * 这与 `checkFundSingleOutlet`（资金出口唯一）是同一件事的两种说法。
 * ⇒ `applySplit()` 在**打开开关的瞬间**就把所有子项方向对齐成父方向，
 * 且**子项行不再提供方向选择** —— UI 上不给出犯这个错的机会。
 *
 * ## 「工资条」为什么仍是同级
 *
 * 负责人 2026-10-04：「**工资条另外实现，应该和收入和支出同等级的**」。
 * 理由：工资条的父项虽然也是一笔（实发到账），但它**天然有收有付**
 * （实发是 `in`，个税 / 社保是 `out` / 不计收支）⇒ 它**不满足"一个方向"**，
 * 所以不能挂进收 / 支的拆分开关里，必须独立成档。
 * 对应 `DR-016` §3.3 的腿级 `excludeFromStats` 能力。
 */
export type EntryMode = 'out' | 'in' | 'transfer' | 'salary';

/** 模式的**方向**（决定分类列表取支出还是收入；`transfer` / `salary` 无单一方向） */
export function modeKind(mode: EntryMode): 'expense' | 'income' | null {
  if (mode === 'out') return 'expense';
  if (mode === 'in') return 'income';
  // 🔴 工资条跨方向（实发 `in` + 扣款 `out`）⇒ **没有**单一方向，
  //    分类列表在工资条模式下应由**每条子项自己的方向**决定，不在这里一刀切。
  return null;
}

/** 该模式**是否支持**打开「拆分」开关（只有收 / 支两种日常记账支持） */
export function supportsSplit(mode: EntryMode): boolean {
  return mode === 'out' || mode === 'in';
}

/**
 * 模式的键 —— 🔴 **`form` + `split` 的合成**，也是 React 里重挂载表单的依据。
 *
 * 为什么需要它：`out` + 拆分开 / `out` + 拆分关，两者的 `EntryMode` 相同
 * 但**提交形态不同**（`multi` vs `single`）⇒ 单看 `mode` 不足以判断当前表单长什么样。
 */
export function modeKey(mode: EntryMode, split: boolean): string {
  return split && supportsSplit(mode) ? `${mode}+split` : mode;
}

/** 模式是否落在**多腿**形态（拆分 / 工资条） */
export function isMultiMode(mode: EntryMode, split: boolean): boolean {
  return mode === 'salary' || (split && supportsSplit(mode));
}

/**
 * 切换形态时**腿级优惠的留存规则**（批 `115` · T8 · `AC-B5.14`）。
 *
 * 🔴 优惠只对 `out` 腿有语义（`AC-C2.34`：`in` 腿传非 0 服务端**直接拒**）。
 * ⇒ 一笔腿切换后若变成 `in`，它的 `discountText` **必须清掉** ——
 * 否则那份值用户看不见（收入侧不渲染那一格），却会在 `buildRequest` 里报
 * 「收入不能填优惠」（最坏的一类"静默生效"）。
 *
 * ⚠️ **不是"切到某模式就全清"**：工资条里的 `out` 腿**本来就该能填优惠**（`LegRow` 仍在渲染），
 * 无差别清空会把用户刚填的值丢掉 —— 那是另一种静默。规则只跟**腿自己的方向**走。
 */
function discountPatch(
  direction: LegDirection,
  leg: Pick<DraftLeg, 'discountText' | 'discountFromStore'>,
): Pick<DraftLeg, 'discountText' | 'discountFromStore'> {
  // 🔴 `discountFromStore` 必须一起清：留着它会让"切到收入再切回支出"的那一行
  //    在下次填整单优惠时**静默不计**自填（标记是编辑回填专用的，不该跟着模式漂移）。
  return direction === LegDirection.In
    ? { discountText: undefined, discountFromStore: undefined }
    : { discountText: leg.discountText, discountFromStore: leg.discountFromStore };
}

/**
 * 应用模式（**保留已填内容**，见 `switchForm`）。
 *
 * @param split 仅对 `out` / `in` 有意义 —— 打开 = 该方向内多腿（拆分）
 */
export function applyMode(draft: TxnDraft, mode: EntryMode, split = false): TxnDraft {
  /**
   * 🆕 批 `116`（`DR-067`）：**分期只属于「单笔支出 + 信用卡」**。
   * 切到收入 / 转账 / 工资条、或打开拆分 ⇒ 三个分期字段必须清空。
   */
  const keepInstallment = mode === 'out' && !split;
  const clearedInstallment = keepInstallment
    ? {}
    : {
        installmentPeriods: undefined,
        installmentFeeMode: undefined,
        installmentFeeText: undefined,
      };
  /**
   * 🆕 批 141：**「简化记账」标记只属于「收入」单笔**。
   * 切到支出 / 转账 / 工资条 ⇒ 清掉；留在草稿里就是"一笔支出带着工资条标记"这种
   * 用户看不见却会提交的值（与分期三字段同一条纪律）。
   */
  const keepSalaryMode = mode === 'in' && !split ? draft.salaryMode : undefined;
  if (mode === 'out' || mode === 'in') {
    const dir = mode === 'out' ? LegDirection.Out : LegDirection.In;
    // 打开拆分 ⇒ 落 `multi`；否则单笔
    const base = switchForm(draft, split ? 'multi' : 'single');
    return {
      ...base,
      ...clearedInstallment,
      salaryMode: keepSalaryMode,
      /**
       * 🔴 **整单优惠只在「支出 · 拆分」下有意义**（批次 `94`）。
       * 切到收入 / 关掉拆分时**必须清掉** —— 留着是一个用户看不见的值，
       * 却会在提交时把某一笔的优惠改掉（最坏的一类"静默生效"）。
       */
      splitDiscountText: mode === 'out' && split ? draft.splitDiscountText : undefined,
      // 🔴 **锁死同向**：所有子项方向 = 父方向（负责人原话「必须是一个方向」）。
      //    这既满足 `checkFundSingleOutlet`，也让 UI 不必再给子项方向选择。
      legs: base.legs.map((leg) => ({
        ...leg,
        direction: dir,
        // 拆分 / 单笔里没有「不计收支」子项的概念（那是工资条独有的）⇒ 清掉残留
        excludeFromStats: undefined,
        // 🆕 批 `115`（T8）：切到收入 ⇒ 腿级优惠必须清（`in` 腿带优惠服务端直接拒）
        ...discountPatch(dir, leg),
      })),
    };
  }
  if (mode === 'transfer') {
    const t = switchForm(draft, 'transfer');
    return {
      ...t,
      ...clearedInstallment,
      salaryMode: keepSalaryMode,
      splitDiscountText: undefined,
      // 转账的两条腿由 `buildRequest` 自己造（不带 `discount`）⇒ 草稿里的占位腿一并清干净
      legs: t.legs.map((leg) => ({ ...leg, discountText: undefined, discountFromStore: undefined })),
    };
  }

  // ── 工资条：多腿、**可跨方向**、可含不计收支子项（`DR-016` §3.3）──
  const multi = switchForm(draft, 'multi');
  return {
    ...multi,
    ...clearedInstallment,
    salaryMode: keepSalaryMode,
    splitDiscountText: undefined,
    // 🔴 工资条内**每笔保留各自方向**（实发 `in` + 扣款 `out` 天然有收有付），
    //    这里只保证「第一笔」是收入（= 实发到账），不强制改写其余笔。
    legs: multi.legs.map((leg, i) => {
      const direction = i === 0 ? LegDirection.In : leg.direction;
      return {
        ...leg,
        direction,
        ...(i === 0 ? { excludeFromStats: undefined } : {}),
        // 🆕 变成 `in` 的那一笔（首笔实发）不能留优惠；其余 `out` 腿保留（它们仍可填）
        ...discountPatch(direction, leg),
      };
    }),
  };
}

// ─────────────────────── 编辑：服务端详情 → 草稿（`AC-C1.41`） ───────────────────────

/** `editDraft()` 的结果 —— 草稿 + 由草稿派生的**界面模式**（`mode` / `split` 决定渲染哪套 UI） */
export interface EditDraftResult {
  draft: TxnDraft;
  mode: EntryMode;
  split: boolean;
}

/**
 * 把服务端**详情**反序列化成草稿（编辑路径 · `DR-042` D1 · `AC-C1.41`）。
 *
 * ## 🔴 预填完整性 = 本路径的**唯一防线**
 * `legs` 在服务端是**整组替换** ⇒ 少预填一条腿的任一**可改字段**，用户"原样保存"时
 * 那一项就**静默丢失**（`AC-C1.42`）。⇒ 每条腿的**全部可改字段**都要搬：
 * 账户 / 方向 / **金额（实付）** / **优惠** / 分类 / **腿级备注** / **腿级不计收支**。
 *
 * ## 🔴 批 `147` 起 `tagIds` **必须搬**（改口的地方）
 *
 * 修前本注释写的是「不搬 `tagIds` / `status` / `parentId`，因为编辑页没有它们的入口」——
 * 那个理由在批 `147` 给本页加了**标签入口**之后**不再成立**。
 * 现在 `tagIds` 与 `legs` 同性质（都是本页可改、且服务端整体替换）：
 * 不搬 ⇒ 打开编辑页看到空标签、用户勾选后保存的却是"原来的 + 新的"或"一保存就清空"，
 * 两种都错。`status` / `parentId` 仍然**没有入口**，继续"不发 = 不改"。
 *
 * ## 🔴 金额用 `centsToYuanText`（整数分 → 元的精确文本）
 * 不用 `/ 100` 浮点：`1.05` 这类值会出现 `1.0499999…`（`money.ts` 文件头记过同类坑）。
 */
export function editDraft(detail: TransactionDetailView): EditDraftResult {
  const legs = detail.legs;
  const first = legs[0];
  const tagIds = detail.tags.map((t) => t.id);
  const dirMode: EntryMode = first?.direction === LegDirection.In ? 'in' : 'out';

  // ── 转账：2 腿（`out` + `in`），金额**两端共用一个数** ──
  if (detail.parent.kind === ParentRowKind.Transfer) {
    const out = legs.find((l) => l.direction === LegDirection.Out) ?? first;
    const into = legs.find((l) => l.direction === LegDirection.In) ?? first;
    return {
      mode: 'transfer',
      split: false,
      draft: {
        form: 'transfer',
        date: detail.date,
        note: detail.note ?? '',
        tagIds,
        // 🔴 转账不看 `legs`，但草稿类型要求非空 —— 留一条**由转出腿填的**占位腿，
        //    这样用户从"转账"切到"支出"时已有输入（`switchForm` 的保留语义）
        legs: out === undefined ? [emptyLeg('')] : [toLegDraft(out)],
        fromAccountId: out?.accountId ?? '',
        toAccountId: into?.accountId ?? '',
        transferAmountText: out === undefined ? '' : centsToYuanText(toCents(out.amount)),
      },
    };
  }

  /**
   * 形态判定与服务端 `checkCompositeShape()` **同一判据**：
   * **同账户 + 同向 + ≥2 腿 = 拆分**；否则（跨账户 或 跨向）= 工资条。
   * ⚠️ 端侧这里只用来**选渲染哪套 UI**，不改写任何规则 —— 真正的判定仍在服务端。
   */
  const sameAccount = legs.every((l) => l.accountId === first?.accountId);
  const sameDirection = legs.every((l) => l.direction === first?.direction);
  const isSplit = legs.length >= 2 && sameAccount && sameDirection;

  return {
    // 🔴 跨账户/跨向的多腿必须落 `salary` 模式 —— 否则 `isMultiMode()` 为假、页面会渲染成"单笔"
    mode: isSplit ? dirMode : legs.length >= 2 ? 'salary' : dirMode,
    split: isSplit,
    draft: {
      form: legs.length >= 2 ? 'multi' : 'single',
      date: detail.date,
      note: detail.note ?? '',
      tagIds,
      legs: legs.map(toLegDraft),
      fromAccountId: first?.accountId ?? '',
      toAccountId: '',
      transferAmountText: '',
      /**
       * 🆕 批 141：**把「简化记账」标记搬回来**（`null` ⇒ `undefined`，与"没有"同形）。
       *
       * 🔴 漏这一步的后果不是显示问题：用户打开一笔简化工资、只改个金额再存，
       *    草稿里没有标记 ⇒ 走 `PATCH` 时被显式清成 `null` ⇒ **标记悄悄丢了**，
       *    报表的"数据不完整"提示也跟着消失（而这正是这个标记存在的唯一理由）。
       */
      salaryMode: detail.salaryMode ?? undefined,
    },
  };
}

/** 一条腿：服务端视图 → 草稿（**全部可改字段**；漏一个 = 保存即丢一个） */
function toLegDraft(l: TransactionLegView): DraftLeg {
  return {
    accountId: l.accountId,
    direction: l.direction,
    /**
     * 🔴 `DR-084`：回填的是**原价**，不是 `amount`。
     *
     * 库里存的是实付（`amount`）+ 优惠（`discount`）⇒ 原价 = 两者之和
     * （唯一实现 = 共享层 `storedLegOriginal()`）。
     * ⚠️ 直接搬 `amount` 会让"打开编辑页不改任何东西就保存"把支出**改大**优惠那一截。
     */
    amountText: centsToYuanText(storedLegOriginal({ amount: l.amount, discount: l.discount })),
    // 优惠 0 ⇒ 空串（与"用户没填"同形，提交时按剔键处理）
    discountText: l.discount === 0 ? '' : centsToYuanText(toCents(l.discount)),
    /**
     * 🔴 `DR-084` D4：**打上"来自库"的标记**。
     *
     * 用户若在父级重填整单优惠，这些值必须退出自填（否则再扣一次）；
     * 用户手动改这一格 ⇒ 页面侧清标记（变成真自填）。
     */
    discountFromStore: l.discount > 0 ? true : undefined,
    categoryId: l.categoryId,
    note: l.note ?? '',
    // 🔴 腿级不计收支也要搬（工资条里的社保 / 公积金转账腿）—— 漏掉它同样"保存即丢失"
    excludeFromStats: l.excludeFromStats,
  };
}

// ─────────────────────────── 提交映射 ───────────────────────────

export interface BuildContext {
  /** 账户最小投影 —— 只用于「转账两端同币种」判定与文案（**不查余额**） */
  accounts: readonly { id: string; name: string; currency: string }[];
  /**
   * 🆕 **这是在改一笔**（批 141）。
   *
   * 🔴 只影响一类字段：**`undefined` 在 `PATCH` 里是"别动"，在 `POST` 里是"没有"**。
   * 工资条的 `salaryMode` 恰好需要"改一笔时明确清掉"（用户把简化记账改成完整工资条）
   * —— 缺省不传 = 库里那个 `simple` 永远洗不掉，报表就会一直报"数据不完整"。
   */
  editing?: boolean;
}

export type BuildResult =
  | { readonly ok: true; readonly request: CreateTransactionRequest }
  | { readonly ok: false; readonly errors: readonly string[] };

/**
 * 金额文本 → 整数分；**非法返回 `null`**（不抛）。
 *
 * 🔴 这里必须"先判后转"：`yuanTextToCents` 对非法文本**抛 `RangeError`**
 * （那是内核的防御，不是给表单用的错误通道）⇒ 表单侧自己接住它。
 *
 * 🔴 **导出**（㊼ 批起）：流水页的**金额区间筛选**（维度 6）用的是**同一条规则**
 * ——"最多两位小数"这个口径只许有一处。若各写一份正则，迟早一处放宽、一处没放宽，
 * 而两者的失败方式都是**静默**（一个筛得出、一个筛不出）。
 *
 * ## 🔴 负数为什么默认不收（批 `131` 加的白名单开关）
 *
 * **交易录入**的金额栏恒正 —— 方向由 `in` / `out` 另选 ⇒ 这里放行负号等于悄悄多出
 * 一条"用负数表达方向"的写法，与腿的 `direction` **两处各答一遍**"这是收入还是支出"。
 *
 * **周期规则**不同：`schedules.amount` 的符号**本身就是方向**
 * （内核 `directionOfAmount()`：负 = `in`；规格 §18「周期金额可正可负」）⇒
 * 那一处必须能输负数。否则「补贴 / 公积金入账」这类规则**建不出来也改不了** ——
 * 输入框提示写着「补贴等入账记负数」，而保存按钮会因解析失败变成禁用：**静默，连报错都没有**。
 * ⇒ 只给 `ScheduleDialog` 开 `allowNegative`，默认仍是"不收"。
 */
export function yuanTextToCentsOrNull(
  text: string,
  options: { allowNegative?: boolean } = {},
): Cents | null {
  const trimmed = text.trim();
  // 与页面的输入提示保持同一口径：最多两位小数（`yuanTextToCents` 虽会舍入第 3 位，
  // 但"用户打了 3 位小数"应当被提示而不是被静默四舍五入）
  const pattern = options.allowNegative === true ? /^-?\d+(\.\d{1,2})?$/ : /^\d+(\.\d{1,2})?$/;
  if (!pattern.test(trimmed)) return null;
  try {
    return yuanTextToCents(trimmed);
  } catch {
    return null;
  }
}

/** 行的称呼（报错点名用）：多行时「第 N 条」，单笔「本笔」 */
function labelOf(index: number, count: number): string {
  return count > 1 ? `第 ${String(index + 1)} 条` : '本笔';
}

/**
 * 实付必须 > 0（`DR-084` D5）。
 *
 * 🔴 这是**用户输入校验**，所以放在端侧（要指名"第 N 笔"）；共享层只做派生、不判，
 * 契约层 `.positive()` 是最后一道。原价 ≤ 0 的行不在这里报 —— 那是"金额没填"，
 * 由 `buildRequest` 的 `BAD_AMOUNT` 说，避免同一格出两条错。
 */
function pushPayErrors(
  originals: readonly number[],
  pays: readonly number[],
  errors: string[],
): void {
  pays.forEach((p, i) => {
    if ((originals[i] ?? 0) > 0 && p <= 0) {
      errors.push(`${labelOf(i, pays.length)}：优惠不能大于等于原价（实付须大于 0）`);
    }
  });
}

/**
 * 整单优惠的解析 + 分摊结果 —— **界面显示与提交共用同一个结果**（批次 `94` · `DR-084`）。
 *
 * ## 🔴 为什么必须共用一个函数
 *
 * 页面要把「这一行摊到 ¥0.06」显示出来，提交时又要算出落库的 `discount` 与 `amount`。
 * 若两处各算一遍，就会出现**卡面 3.20 / 库里 3.19** —— 偏差只有一分，
 * 但两边都"看起来对"，只能靠对账发现（铁律 24 同型）。
 * ⇒ 取整、分摊、派生实付的唯一实现在共享层 `nucleus/discount.ts`，本函数只做**输入解析 + 文案**。
 *
 * ## 语义（`DR-084` 起）
 *
 * ```
 * 原价_i = 用户填的「原价」格
 * 权重_i = 原价_i                      // 🔴 不再 + 自填（原价已含它）
 * 分摊_i = allocate(整单优惠, 权重)      // Σ 恒等
 * 最终_i = 自填_i + 分摊_i              // 负责人 2026-10-21 拍板：子笔仍可自填
 * 实付_i = 原价_i − 最终_i              // 落库 amount；必须 > 0
 * ```
 */
export interface SplitDiscountResolved {
  ok: boolean;
  errors: string[];
  /** 每行**分摊到**的整单优惠（分），与 `legs` 同序 */
  shares: number[];
  /** 每行**最终**优惠 = 自填 + 分摊（分），落库用这个，与 `legs` 同序 */
  finals: number[];
  /** 每行**实付** = 原价 − 最终优惠（分）—— 落库 `amount` 用这个，与 `legs` 同序（`DR-084` D2） */
  pays: number[];
  /** 整单优惠总额（分） */
  total: number;
  /** 各行原价之和（分）—— 整单优惠的上界 */
  originalTotal: number;
}

/**
 * 解析并分摊整单优惠。
 *
 * 🔴 `wholeText` 为空 ⇒ 退化为"只有各行自填"（`finals` = 自填、`shares` 全 0）——
 * 这样调用方**不需要分支**，单笔与拆分走同一条代码路径。
 *
 * @param legs 当前草稿的腿（顺序即落库顺序 —— 平局取整按索引升序，顺序变了结果会变）
 * @param wholeText 整单优惠的十进制字面量（空串 = 没填）
 */
export function resolveSplitDiscount(
  legs: readonly DraftLeg[],
  wholeText: string,
): SplitDiscountResolved {
  const errors: string[] = [];
  const zeros = legs.map(() => 0);
  const wholeGiven = wholeText.trim() !== '';

  // ① 逐行：原价 + 自填优惠
  const selfs: number[] = [];
  const originals: number[] = [];
  legs.forEach((leg, i) => {
    const label = labelOf(i, legs.length);
    const selfText = (leg.discountText ?? '').trim();
    const self = selfText === '' ? 0 : yuanTextToCentsOrNull(selfText);
    if (self === null || self < 0) {
      errors.push(`${label}：优惠格式不对（最多两位小数、不得为负）`);
      selfs.push(0);
    } else if (wholeGiven && leg.discountFromStore === true) {
      /**
       * 🔴 `DR-084` D4：**回填值退出自填**。
       *
       * 编辑一笔带优惠的拆分时，行内那一格是「上次自填 + 上次分摊」的合流结果。
       * 用户若要重填整单优惠，意图显然是"重新摊一遍"，而不是"在旧值上再加一次"
       * ⇒ 带 `discountFromStore` 标记的值按 0 计；用户手动改过这一格 ⇒ 标记已清 ⇒ 正常叠加。
       */
      selfs.push(0);
    } else {
      selfs.push(self);
    }
    originals.push(yuanTextToCentsOrNull(leg.amountText) ?? 0);
  });

  const selfPays = originals.map((o, i) => o - (selfs[i] ?? 0));
  const base: SplitDiscountResolved = {
    ok: false,
    errors,
    shares: zeros,
    finals: selfs,
    pays: selfPays,
    total: 0,
    originalTotal: originals.reduce((a, b) => a + b, 0),
  };

  // ② 整单优惠本身
  const text = wholeText.trim();
  if (text === '') {
    // 不填整单 ⇒ 退化为"只有各行自填"（`pays` = 原价 − 自填），调用方无需分支
    pushPayErrors(originals, selfPays, errors);
    return { ...base, ok: errors.length === 0, errors };
  }
  const total = yuanTextToCentsOrNull(text);
  if (total === null || total < 0) {
    errors.push('整单优惠格式不对（最多两位小数、不得为负）');
    return { ...base, errors };
  }
  if (legs.length < 2) {
    // 🔴 单笔不提供这一格 —— 若从旧状态残留了值，宁可说清也不静默忽略
    errors.push('整单优惠只在拆分时用得上；单笔请直接填那一笔的优惠');
    return { ...base, errors };
  }
  if (errors.length > 0) return { ...base, errors };

  const lines = legs.map((_leg, i) => ({
    original: toCents(originals[i] ?? 0),
    selfDiscount: toCents(selfs[i] ?? 0),
  }));
  const orig = sumOriginals(lines);
  if ((orig as number) === 0) {
    errors.push('整单优惠无法分摊：各行原价之和为 0');
    return { ...base, errors };
  }
  if ((total as number) > (orig as number)) {
    errors.push(
      `整单优惠不能超过各行原价之和（最高 ${centsToYuanText(toCents(orig as number))}）`,
    );
    return { ...base, errors };
  }

  const finals = finalDiscounts(lines, total);
  const pays = paidAmounts(lines, total);
  // 🔴 `DR-084` D5：每行实付必须 > 0（优惠 ≥ 原价 ⇒ 契约 `.positive()` 必拒，端侧先说清）
  pushPayErrors(originals, pays, errors);
  if (errors.length > 0) return { ...base, total: total as number };
  return {
    ok: true,
    errors: [],
    shares: finals.map((f, i) => (f as number) - (selfs[i] ?? 0)),
    finals: finals.map((f) => f as number),
    pays: pays.map((p) => p as number),
    total: total as number,
    originalTotal: orig as number,
  };
}

const NO_ACCOUNT = '请选择账户';const BAD_AMOUNT = '金额必填，最多两位小数';

/**
 * 🆕 批 `116`（`DR-067`）：把草稿里的三个分期字段解析成契约的 `installment`。
 *
 * ## 🔴 这里只做三件事，**一句分期口径都不写**
 *
 * | 做什么 | 为什么在这里 |
 * |---|---|
 * | 判"这笔能不能分期"（单笔 / 支出 / 非退款 / 非垫付） | 页面已经用渲染条件挡了同一批情况；这里是**第二道**，防止"切换模式后残留"的静默提交 |
 * | 期数取值域 2–60 | 与契约一致（契约层还有一次）；端侧先说清，省一次 400 |
 * | 手续费文本 → 整数分 | 金额解析的唯一入口是 `yuanTextToCentsOrNull`（不带第二份正则） |
 *
 * 🔴 **本金与首期账单日不参与**：那是服务端的事（`DR-067` D2①②）。
 *
 * @returns `value === undefined` ⇒ 不分期（请求里**不出现** `installment` 键）
 */
export function resolveInstallment(draft: TxnDraft): {
  ok: boolean;
  errors: readonly string[];
  value?: { periods: number; feeMode: 'total' | 'per_period'; feeValue: number };
} {
  const periods = draft.installmentPeriods;
  if (periods === undefined) return { ok: true, errors: [] };

  const errors: string[] = [];
  const leg = draft.legs[0];
  if (draft.form !== 'single' || leg === undefined || leg.direction !== LegDirection.Out) {
    errors.push('分期只适用于单笔支出（切换记账方式后请重新打开分期开关）');
  }
  if ((draft.advanceAccountId ?? '').trim() !== '') {
    errors.push('「垫付到…」与分期不能同时用 —— 垫付会给本笔多记一条应收，就不再是单笔支出了');
  }
  if (draft.refundParentId !== null && draft.refundParentId !== undefined) {
    errors.push('退款不能再分期（退款是冲减一笔已有支出）');
  }
  if (!Number.isInteger(periods) || periods < 2 || periods > 60) {
    errors.push('期数须在 2–60 之间');
  }
  const feeText = (draft.installmentFeeText ?? '').trim();
  const feeValue = feeText === '' ? 0 : yuanTextToCentsOrNull(feeText);
  if (feeValue === null || feeValue < 0) {
    errors.push('手续费格式不对（最多两位小数、不得为负）');
  }
  if (errors.length > 0 || feeValue === null) return { ok: false, errors };

  return {
    ok: true,
    errors: [],
    value: { periods, feeMode: draft.installmentFeeMode ?? 'total', feeValue },
  };
}

/** 组装一条腿（账户/金额都已在调用方校验过） */
function toShapeLeg(leg: DraftLeg, cents: Cents): ShapeLeg {
  return {
    accountId: leg.accountId,
    direction: leg.direction,
    amount: cents,
    categoryId: leg.categoryId,
    // 🔴 形态判定（`checkLegShape`）**不用** `categoryKind`（服务端那条路径同样传 `null`）——
    //    它只在退款判定（`isRefundTxn`）里用，而复刻退款不在本页职责内（C 域 §5.2.3 需先选原交易）
    categoryKind: null,
    // 腿级排除（工资条里的社保 / 公积金转账子项）—— 缺省 = 计入
    excludeFromStats: leg.excludeFromStats === true,
  };
}

export function buildRequest(draft: TxnDraft, ctx: BuildContext): BuildResult {
  const errors: string[] = [];
  const note = draft.note.trim() === '' ? null : draft.note.trim();
  /**
   * 🆕 批 `116`：分期参数**先解析再分派** —— 转账 / 多腿分支也走同一道闸，
   * 这样"切走模式后残留的选择"不会在某个分支被**静默忽略**。
   */
  const installment = resolveInstallment(draft);
  if (!installment.ok) return { ok: false, errors: installment.errors };

  if (draft.form === 'transfer') {
    // ── 转账：2 腿（from `out` + to `in`），**交易级不计收支** ──
    if (draft.fromAccountId === '' || draft.toAccountId === '') errors.push('请选择转出与转入账户');
    if (draft.fromAccountId !== '' && draft.fromAccountId === draft.toAccountId) {
      errors.push('转出与转入账户不能是同一个');
    }
    const cents = yuanTextToCentsOrNull(draft.transferAmountText);
    if (cents === null || cents <= 0) errors.push(BAD_AMOUNT);

    const from = ctx.accounts.find((a) => a.id === draft.fromAccountId);
    const to = ctx.accounts.find((a) => a.id === draft.toAccountId);
    if (from !== undefined && to !== undefined && from.currency !== to.currency) {
      // 🔴 P0 范围限制，**不是**口径判定 —— 跨币种转账有专门的腿结构（`DR-004` §2.5）
      errors.push(
        `跨币种转账暂不支持（${from.name} 是 ${from.currency}、${to.name} 是 ${to.currency}）——`
          + '请分别记录两端，或等汇率域的范围扩展',
      );
    }
    if (errors.length > 0 || cents === null) return { ok: false, errors };

    const legs: ShapeLeg[] = [
      toShapeLeg(
        { accountId: draft.fromAccountId, direction: LegDirection.Out, amountText: '', categoryId: null },
        cents,
      ),
      toShapeLeg(
        { accountId: draft.toAccountId, direction: LegDirection.In, amountText: '', categoryId: null },
        cents,
      ),
    ];
    // 🔴 即使本页只产生 2 腿，也**必须**过一遍内核（否则"端侧自认为合法"就与服务端各判一套）
    const issues = checkLegShape(legs, true);
    if (issues.length > 0) return { ok: false, errors: issues };

    return {
      ok: true,
      request: {
        date: draft.date,
        note,
        // 🆕 批 `147`：标签整体替换（新建传空数组 = 没有标签；编辑传的就是用户最终选的集合）
        tagIds: [...draft.tagIds],
        legs: legs.map((l) => ({
          accountId: l.accountId,
          direction: l.direction,
          amount: l.amount,
          categoryId: null,
          excludeFromStats: false,
        })),
        // 转账整笔不进收支（`DR-004`：跨币种同样；同币种更是）
        excludeFromStats: true,
      },
    };
  }

  // ── 单腿 / 多腿 ──
  if (draft.legs.length === 0) errors.push('至少要有一条腿');
  /**
   * 🆕 整单优惠（批次 `94`）：**解析 + 分摊一次算清**。
   *
   * 🔴 页面上那一行「摊到 ¥3.20」与下面落库的 `discount` 都读**同一个结果**
   * （`resolveSplitDiscount` ⇒ 共享层 `finalDiscounts`）—— 不在这里再算一遍取整。
   */
  const whole = resolveSplitDiscount(draft.legs, draft.splitDiscountText ?? '');
  errors.push(...whole.errors);
  const shape: ShapeLeg[] = [];
  const payloadLegs: CreateTransactionRequest['legs'] = [];
  draft.legs.forEach((leg, i) => {
    const label = labelOf(i, draft.legs.length);
    if (leg.accountId === '') errors.push(`${label}：${NO_ACCOUNT}`);
    // 🔴 `DR-084`：这一格是**原价**（不是实付）
    const originalCents = yuanTextToCentsOrNull(leg.amountText);
    if (originalCents === null) errors.push(`${label}：${BAD_AMOUNT}`);
    else if (originalCents <= 0) errors.push(`${label}：原价需大于 0（方向由「收入 / 支出」表达）`);
    /**
     * 🆕 `DR-084`：**落库的两个数都在这里取自共享层那一次计算**。
     *
     * `discount = 自填 + 分摊`；`amount = 原价 − discount`（实付）。
     * 🔴 端侧**不重算**取整与减法 —— 否则界面显示与落库各算一遍（铁律 24）。
     */
    const discCents = whole.finals[i] ?? 0;
    const payCents = whole.pays[i] ?? 0;
    // 🔴 收入侧不得带优惠（`AC-C2.34`：`in` 腿传非 0 服务端拒绝）——
    //    界面已不渲染那一格，这里挡住"从编辑态残留过来"的情况
    if (leg.direction === LegDirection.In && discCents !== 0) {
      errors.push(`${label}：收入不能填优惠（优惠只对支出有意义）`);
    }
    // 🆕 子笔备注（交易级备注在 `draft.note`，两者互不覆盖）
    const legNote = (leg.note ?? '').trim();
    // 实付 ≤ 0 已由 `resolveSplitDiscount` 点名报过（`DR-084` D5），此处只拦"填不全"的行
    if (leg.accountId === '' || originalCents === null || originalCents <= 0 || payCents <= 0) {
      return;
    }
    // 🔴 形态判定喂的是**落库的那个数**（实付）—— 与服务端看到的完全一致
    shape.push(toShapeLeg(leg, toCents(payCents)));
    payloadLegs.push({
      accountId: leg.accountId,
      direction: leg.direction,
      amount: payCents,
      categoryId: leg.categoryId,
      excludeFromStats: leg.excludeFromStats === true,
      // 🔴 可选键**按需剔键**（铁律 63 / `AC-C1.4x`）：优惠 0 与空备注**不发该键**，
      //    避免 `discount: undefined` 这种"看起来发了其实没发"的形状。
      ...(discCents === 0 ? {} : { discount: discCents }),
      ...(legNote === '' ? {} : { note: legNote }),
    });
  });
  if (errors.length > 0) return { ok: false, errors };

  // 🆕 垫付专线（`DR-048` D7 · `AC-Q3.2`）：追加一条 `contra` 的 `in` 腿（金额**恒等于**主腿）。
  //
  // 🔴 三条边界都**明说**而不是静默忽略：静默忽略会让用户以为"垫付关系记上了"，
  //    而实际上那笔钱在报销账户里根本不存在（报销时"该账户没有垫付记录"）。
  const advanceAccountId = (draft.advanceAccountId ?? '').trim();
  if (advanceAccountId !== '') {
    const main = draft.legs[0];
    if (draft.form !== 'single' || draft.legs.length !== 1) {
      errors.push('「垫付到…」只能用于单笔支出（拆分请把每一笔分别记）');
    } else if (main === undefined || main.direction !== LegDirection.Out) {
      errors.push('只有支出可以「垫付到…」（收入不是垫付）');
    } else if (advanceAccountId === main.accountId) {
      errors.push('垫付账户不能与本笔支出的账户相同（同一账户一进一出等于什么都没记）');
    } else {
      const cents = yuanTextToCentsOrNull(main.amountText);
      if (cents !== null && cents > 0) {
        const advanceLeg: DraftLeg = {
          accountId: advanceAccountId,
          direction: LegDirection.In,
          amountText: main.amountText,
          // 🔴 借贷账户上不挂分类（`AC-Q6.1`）；分类只属于真正计入收支的那条腿
          categoryId: null,
          // 🔴 本金往来一律不计收支（`AC-Q1.4`）
          excludeFromStats: true,
        };
        shape.push(toShapeLeg(advanceLeg, cents));
        payloadLegs.push({
          accountId: advanceLeg.accountId,
          direction: advanceLeg.direction,
          amount: cents,
          categoryId: null,
          excludeFromStats: true,
        });
      }
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  // 🔴 形态判定的**唯一入口**（腿数上限按账户数分派 → 资金出口唯一 → 转账形态）
  const issues = checkLegShape(shape, false);
  if (issues.length > 0) return { ok: false, errors: issues };

  // 🔴 退款专线（`DR-035` v1.1 · C 域 PRD §5.2.3）：只有**单笔**才谈得上"冲减一笔原支出"。
  //    多腿（拆分 / 工资条）带 `parentId` 会被服务端判 400（"拆分请用一笔交易的多条腿一次录入"）
  //    ⇒ 与其发出去挨 400，不如在端侧就说清为什么不行。
  const refundParentId = draft.refundParentId ?? null;
  if (refundParentId !== null && draft.legs.length > 1) {
    return { ok: false, errors: ['退款只能记成一笔（冲减的是一笔具体支出），不能同时拆分或多腿'] };
  }
  /**
   * 🆕 批 `119`：**退款必须带分类**（而且得是原来的支出分类）。
   *
   * 内核 `isRefundTxn()` 的判据是「`in` 腿 + 至少一条挂**支出**分类」——
   * 不挂分类的 `in` 腿只是一笔"未分类收入"，服务端 `bindRefundToParent()` 会 400。
   * 端侧先拦是因为那句话（"`parentId` 只能用于退款"）对用户毫无信息量。
   *
   * ⚠️ 端侧**只判"有没有"，不判 kind**：分类的支出 / 收入归属由服务端 join 分类表得出，
   * 端侧再判一次就是第二份口径（铁律 24）。
   */
  if (refundParentId !== null && (payloadLegs[0]?.categoryId ?? null) === null) {
    return {
      ok: false,
      errors: ['退款要选原来的支出分类 —— 退款靠「收入 + 支出分类」才被认成冲减，不选分类会被记成普通收入'],
    };
  }

  return {
    ok: true,
    request: {
      date: draft.date,
      note,
      // 🆕 批 `147`：标签**整体替换**（契约与 `PATCH` 同纪律）。
      //    🔴 必须**总是发**：`editDraft` 已把原标签搬进草稿 ⇒ 用户取消勾选就是真的清空；
      //    若做成"空数组不发"，用户永远删不掉最后一个标签。
      tagIds: [...draft.tagIds],
      legs: payloadLegs,
      // 🔴 `parentId` 仅在退款时出现（普通记账传 `null`，语义 = "没有原交易"）
      ...(refundParentId === null ? {} : { parentId: refundParentId }),
      // 🆕 批 141：只在真的是「简化记账」时带该键（可选键按需剔键 —— 铁律 63）
      //    🔴 例外：**改一笔**时必须显式发 `null` —— `PATCH` 里 `undefined` 是"别动"，
      //       不清掉的话"改成完整工资条"之后库里那个 `simple` 永远洗不掉（报表会一直报不完整）。
      ...(draft.salaryMode === undefined
        ? ctx.editing === true
          ? { salaryMode: null }
          : {}
        : { salaryMode: draft.salaryMode }),
      // 🆕 批 `116`：只在真的分期时才带该键（可选键按需剔键 —— 铁律 63）
      ...(installment.value === undefined ? {} : { installment: installment.value }),
    },
  };
}

/**
 * 形态的**展示名** —— 由腿集派生而不由 `form` 决定。
 *
 * 为什么不让标签跟着 `form` 走：用户选了「拆分」也可能录成跨账户有收有付（那就不是拆分了）。
 * ⇒ 标签回答"**这笔账在账本里长什么样**"，判据只看腿
 * （与服务端 `TransactionRowView` 的形态同源）。
 *
 * 🔴 **文案里不出现「腿」**（2026-10-04 改）：腿是 `legs` 表的概念，用户看的是「笔」。
 *
 * 🔴 本版按**方向**区分展示名（对齐"拆分 = 同向、工资条 = 跨向"的新模型）：
 * - 同向且同账户 ⇒ **拆分**（对账单上一笔，账本内部几分）
 * - 有收有付 或 跨账户 ⇒ **工资条**（天然含扣款）
 */
export function describeShape(draft: TxnDraft): string {
  if (draft.form === 'transfer') return '转账（两个账户各记一笔 · 不计收支）';
  if (draft.form === 'single') return '单笔收支';
  const n = draft.legs.length;
  const dirs = new Set(draft.legs.map((l) => l.direction));
  const accounts = new Set(draft.legs.map((l) => l.accountId).filter((a) => a !== ''));
  if (dirs.size === 1 && accounts.size <= 1) {
    const dir = draft.legs[0]?.direction === LegDirection.In ? '收入' : '支出';
    return `拆分（${String(n)} 笔${dir} · 同一账户）`;
  }
  return `工资条（${String(n)} 笔 · 含扣款或有多个账户）`;
}
